import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { groupSessionService } from '../api/services';
import { useAuth } from '../context/AuthContext';
import {
  Mic, MicOff, Video, VideoOff, PhoneOff,
  Users, MessageCircle, Loader2, CheckCircle2,
  AlertCircle, Share2, Copy,
} from 'lucide-react';
import toast from 'react-hot-toast';

const GroupSessionRoomPage = () => {
  const { id }      = useParams();
  const { user }    = useAuth();
  const navigate    = useNavigate();
  const iframeRef   = useRef(null);

  const [session,    setSession]    = useState(null);
  const [tokenData,  setTokenData]  = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [completing, setCompleting] = useState(false);
  const [copied,     setCopied]     = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        // Get session details
        const sessionRes = await groupSessionService.getById(id);
        // ApiResponse.success -> res.data.data.session or res.data.data
        const resData = sessionRes.data?.data;
        const sess = resData?.session ?? (resData && !Array.isArray(resData) && resData.id ? resData : null)
          ?? sessionRes.data?.session;
        setSession(sess);

        // Get join token - ApiResponse.success -> res.data.data has { token, roomUrl, roomName, session }
        const tokenRes = await groupSessionService.getJoinToken(id);
        const td = tokenRes.data?.data ?? tokenRes.data;
        setTokenData(td);
      } catch (err) {
        setError(err.response?.data?.message || 'Could not join session');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [id]);

  const handleComplete = async () => {
    if (!window.confirm('Mark this group session as complete for everyone?')) return;
    setCompleting(true);
    try {
      await groupSessionService.complete(id);
      toast.success('Session completed!');
      navigate('/group-sessions');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not complete session');
      setCompleting(false);
    }
  };

  const handleLeave = () => {
    if (window.confirm('Leave this group session?')) {
      navigate('/group-sessions');
    }
  };

  const handleCopyCode = () => {
    if (session?.joinCode) {
      navigator.clipboard.writeText(session.joinCode);
      setCopied(true);
      toast.success('Join code copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const isHost = session?.hostUserId === user?.id;
  const isExpert = session?.participants?.find(p => p.userId === user?.id)?.role === 'EXPERT';
  const canComplete = isHost || isExpert;

  // Daily.co room URL with token
  const roomUrl = tokenData?.token && tokenData?.roomUrl
    ? `${tokenData.roomUrl}?t=${tokenData.token}`
    : null;

  if (loading) return (
    <div className="min-h-screen bg-forest-950 flex items-center justify-center">
      <div className="text-center space-y-3">
        <Loader2 size={36} className="animate-spin text-forest-400 mx-auto" />
        <p className="text-forest-400 text-sm">Joining group session...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-forest-950 flex items-center justify-center px-4">
      <div className="text-center space-y-4 max-w-sm">
        <AlertCircle size={40} className="mx-auto text-rust-400" />
        <h3 className="font-serif text-xl text-cream-50">Could not join session</h3>
        <p className="text-sm text-forest-400">{error}</p>
        <button onClick={() => navigate('/group-sessions')}
          className="btn-primary">Back to Group Sessions</button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-forest-950 flex flex-col">

      {/* Top bar */}
      <div className="flex items-center justify-between px-5 py-3 bg-forest-900 border-b border-forest-800">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="text-xs font-semibold text-green-400 uppercase tracking-widest">Live</span>
          </div>
          <div className="h-4 w-px bg-forest-700" />
          <span className="text-sm font-medium text-cream-50 truncate max-w-xs">{session?.title}</span>
        </div>

        <div className="flex items-center gap-3">
          {/* Participant count */}
          <div className="flex items-center gap-1.5 text-xs text-forest-400">
            <Users size={13} />
            <span>{session?.participants?.length ?? 0}/{session?.maxParticipants}</span>
          </div>

          {/* Join code chip — for quick sharing */}
          {session?.joinCode && (
            <button onClick={handleCopyCode}
              className="flex items-center gap-2 bg-forest-800 hover:bg-forest-700 border border-forest-700 rounded-lg px-3 py-1.5 transition-colors">
              <span className="font-mono text-xs font-bold text-cream-50 tracking-widest">{session.joinCode}</span>
              {copied
                ? <CheckCircle2 size={12} className="text-green-400" />
                : <Copy size={12} className="text-forest-400" />}
            </button>
          )}

          {/* Complete button — host/expert only */}
          {canComplete && (
            <button onClick={handleComplete} disabled={completing}
              className="flex items-center gap-1.5 text-xs font-semibold bg-green-600 hover:bg-green-500 text-white px-4 py-2 rounded-full transition-colors disabled:opacity-60">
              {completing ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
              Complete Session
            </button>
          )}

          {/* Leave */}
          <button onClick={handleLeave}
            className="flex items-center gap-1.5 text-xs font-semibold bg-rust-500 hover:bg-rust-600 text-white px-4 py-2 rounded-full transition-colors">
            <PhoneOff size={13} /> Leave
          </button>
        </div>
      </div>

      {/* Daily.co iframe */}
      <div className="flex-1 relative">
        {roomUrl ? (
          <iframe
            ref={iframeRef}
            src={roomUrl}
            allow="camera; microphone; fullscreen; display-capture; autoplay"
            className="absolute inset-0 w-full h-full border-0"
            title="Group Session Room"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center space-y-3">
              <Loader2 size={32} className="animate-spin text-forest-400 mx-auto" />
              <p className="text-forest-400 text-sm">Setting up room...</p>
            </div>
          </div>
        )}
      </div>

      {/* Bottom info bar */}
      <div className="px-5 py-3 bg-forest-900 border-t border-forest-800 flex items-center justify-between">
        <p className="text-xs text-forest-500">
          {session?.duration} min session · Invite with code{' '}
          <span className="font-mono font-bold text-forest-300">{session?.joinCode}</span>
        </p>
        <p className="text-xs text-forest-500">
          {new Date(session?.scheduledAt).toLocaleString('en-GH', { timeStyle: 'short', dateStyle: 'short' })}
        </p>
      </div>
    </div>
  );
};

export default GroupSessionRoomPage;